/** Namespace `settings` — wird befüllt während der Komponenten-Migration. */
export const settings = {
  de: {
    settings: {
      title: 'Settings',
      subtitle: 'System-Einstellungen für SeaDent Dental Practice Management.',
      loading: 'Lade Einstellungen…',
      saving: 'Speichere…',
      sending: 'Sende…',
      selectAreaLabel: 'Einstellungs-Bereich wählen',
      tabsAria: 'Settings-Tabs',
      tabEmail: '✉️ Email',
      tabEmailTemplates: '📝 E-Mail-Texte',
      tabUsers: '👥 Benutzer',
      tabGcal: '📅 Google Calendar',
      tabBranding: '🎨 Branding',
      tabBilling: '🧾 Rechnungsstellung',
      tabSelfservice: '🔁 Self-Service',
      tabClosure: '🌴 Urlaub',
      tabProviders: '🩺 Behandler',
      tabProcedures: '😁 Behandlungen',
      tabNoteTemplates: '📋 Doku-Vorlagen',
      tabOperatories: '🏢 Behandlungsräume',
      tabFeatures: '📦 Features',
      // Vendor-Feature-Flags (Paket-Steuerung, nur VENDOR_USERS)
      features: {
        title: 'Gebuchte Features (Paket-Steuerung)',
        description: 'Anbieter-Ansicht: steuert, welche Module diese Installation gebucht hat. Deaktivierte Module verschwinden aus Navigation und Routen; zugehörige Hintergrund-Jobs pausieren. Praxis-Benutzer sehen diesen Tab nicht.',
        communications: 'Mail-Versand aus der Patientenakte',
        hintReminders: 'Inklusive stündlichem Erinnerungs-Cron.',
        hintRecalls: 'Inklusive täglichem Recall-Cron.',
        hintCommunications: 'Compose/Antworten; der Posteingang hat ein eigenes Flag.',
        hintInbox: 'IMAP-Abruf des Praxis-Postfachs inklusive Ingest-Cron.',
        hintGcal: 'Sync + automatischer Kalender-Import.',
        smartai: 'KI-Rezeption (Website-Panel)',
        hintSmartai: 'Interaktives Panel auf der Public Site: Termin, Rückruf, Rezept & Fragen (Wissensbasis).',
        hint: 'Änderungen wirken sofort für alle Benutzer dieser Installation — ohne Neustart. Bestehende Daten bleiben erhalten und erscheinen wieder, sobald das Modul erneut aktiviert wird.',
        saved: 'Features gespeichert.',
        packages: {
          title: 'Paket wählen',
          active: 'Aktives Paket',
          website: 'Praxis-Website',
          websiteHint: '39 €/Monat — nur Website, keine Admin-Module',
          booking: 'Website + Online-Buchung',
          bookingHint: '69 €/Monat — Termine, Kalender, Google-Sync, Reminder',
          platform: 'Komplette Plattform',
          platformHint: '99 €/Monat — alle Module',
          hint: 'Ein Klick setzt und speichert alle Modul-Schalter des Pakets sofort. Einzelne Module lassen sich danach unten weiter anpassen.',
        },
      },
      // Email-Versand (SMTP)
      smtpTitle: 'Email-Versand (SMTP)',
      smtpDescription: 'Konfiguration für ausgehende Mails (Terminbestätigungen, Stornierungen). All-Inkl-Mail-Konto unter <code>info@seadent.es</code>.',
      host: 'Host',
      port: 'Port',
      portHint: '587 = STARTTLS (Hetzner-tauglich), 465 = SSL (von Hetzner geblockt).',
      user: 'Benutzer',
      password: 'Passwort',
      passwordStored: '(gespeichert)',
      passwordPlaceholderStored: '•••••• (leer lassen → unverändert)',
      passwordPlaceholderEnter: 'Passwort eingeben',
      secureHint: 'SSL direkt nutzen (<code>secure=true</code>) — nur sinnvoll bei Port 465. Bei 587 bitte ausgelassen lassen (STARTTLS).',
      // Test-Email
      testEmailTitle: 'Test-Email senden',
      testEmailDescription: 'Prüft die SMTP-Verbindung mit den aktuell <strong>gespeicherten</strong> Werten und sendet eine kleine Test-Mail.',
      testRecipientPlaceholder: 'empfaenger@example.com',
      sendTest: 'Test senden',
      // Toasts / Fehler
      errorLoadEmail: 'Konnte Email-Einstellungen nicht laden — Defaults vorausgefüllt.',
      emailSaved: 'Email-Einstellungen gespeichert.',
      errorSave: 'Speichern fehlgeschlagen.',
      errorTestRecipient: 'Bitte eine Empfänger-Adresse für den Test angeben.',
      testAccepted: 'Test-Email akzeptiert ({{id}}).',
      errorTest: 'Test fehlgeschlagen.',
      errorTestDetail: 'Test fehlgeschlagen: {{detail}}',
      // Self-Service (Patienten sagen ab / verschieben per Link)
      selfservice: {
        title: 'Patienten-Self-Service',
        description: 'Patienten können ihren Termin über den „Termin verwalten"-Link in Bestätigungs- und Erinnerungs-Mails selbst absagen oder verschieben. Hier werden Fristen und Limits festgelegt.',
        enabled: 'Self-Service aktiv (Links in den Mails funktionieren)',
        cancelMinHours: 'Absage-Frist (Stunden)',
        cancelMinHoursHint: 'Online-Absagen sind bis so viele Stunden vor dem Termin möglich.',
        rescheduleMinHours: 'Umbuchungs-Frist (Stunden)',
        rescheduleMinHoursHint: 'Online-Verschieben ist bis so viele Stunden vor dem Termin möglich.',
        maxReschedules: 'Max. Verschiebungen',
        maxReschedulesHint: 'So oft darf ein Patient denselben Termin verschieben.',
        linkHint: 'Innerhalb der Frist sehen Patienten stattdessen die Telefonnummer der Praxis. Bei Deaktivierung zeigt die Seite nur noch die Termindetails an.',
        saved: 'Self-Service-Einstellungen gespeichert.',
        errorLoad: 'Konnte Self-Service-Einstellungen nicht laden — Defaults vorausgefüllt.',
      },
      // Urlaub / Betriebsferien (Online-Buchung zeitweise sperren)
      closure: {
        title: 'Urlaub / Betriebsferien',
        description: 'Sperrt die Terminbuchung über die Website — Buchungs-Wizard, KI-Panel und die Umbuchung per Self-Service-Link. Besucher sehen stattdessen einen Hinweis mit Telefon und WhatsApp. Im Admin bleibt alles wie gewohnt buchbar, telefonische Termine sind also nicht betroffen.',
        enabled: 'Online-Buchung im angegebenen Zeitraum sperren',
        from: 'Ab (optional)',
        fromHint: 'Leer lassen = ab sofort. Mit Datum lässt sich der Urlaub im Voraus eintragen.',
        until: 'Bis einschließlich',
        untilHint: 'Letzter gesperrter Tag. Danach ist die Online-Buchung von selbst wieder offen.',
        statusActive: 'Aktiv — die Online-Buchung ist bis einschließlich {{until}} gesperrt.',
        statusUpcoming: 'Geplant — die Sperre greift ab {{from}} und endet mit dem {{until}}.',
        statusInactive: 'Nicht aktiv — die Online-Buchung läuft normal.',
        statusExpired: 'Der hinterlegte Zeitraum ist vorbei — die Online-Buchung läuft wieder normal. Der Schalter darf angeschaltet bleiben.',
        noticeHint: 'Notfalltermine sind ebenfalls gesperrt: online geht nichts, telefonisch alles. Bereits gebuchte Termine bleiben bestehen und werden nicht abgesagt.',
        saved: 'Urlaubs-Einstellungen gespeichert.',
        errorLoad: 'Konnte die Urlaubs-Einstellungen nicht laden — Defaults vorausgefüllt.',
      },
    },
  },
  es: {
    settings: {
      title: 'Ajustes',
      subtitle: 'Ajustes del sistema para SeaDent Dental Practice Management.',
      loading: 'Cargando ajustes…',
      saving: 'Guardando…',
      sending: 'Enviando…',
      selectAreaLabel: 'Seleccionar el área de ajustes',
      tabsAria: 'Pestañas de ajustes',
      tabEmail: '✉️ Email',
      tabEmailTemplates: '📝 Textos de correo',
      tabUsers: '👥 Usuarios',
      tabGcal: '📅 Google Calendar',
      tabBranding: '🎨 Marca',
      tabBilling: '🧾 Facturación',
      tabSelfservice: '🔁 Autoservicio',
      tabClosure: '🌴 Vacaciones',
      tabProviders: '🩺 Doctores',
      tabProcedures: '😁 Tratamientos',
      tabNoteTemplates: '📋 Plantillas de documentación',
      tabOperatories: '🏢 Salas de tratamiento',
      tabFeatures: '📦 Features',
      // Feature-Flags del proveedor (control de paquetes, solo VENDOR_USERS)
      features: {
        title: 'Features contratados (control de paquetes)',
        description: 'Vista del proveedor: controla qué módulos tiene contratados esta instalación. Los módulos desactivados desaparecen de la navegación y las rutas; los trabajos en segundo plano asociados se pausan. Los usuarios de la clínica no ven esta pestaña.',
        communications: 'Envío de correo desde la ficha del paciente',
        hintReminders: 'Incluye el cron de recordatorios (cada hora).',
        hintRecalls: 'Incluye el cron diario de recalls.',
        hintCommunications: 'Redactar/responder; la bandeja de entrada tiene su propio flag.',
        hintInbox: 'Recogida IMAP del buzón de la clínica incl. cron de ingesta.',
        hintGcal: 'Sincronización + importación automática del calendario.',
        smartai: 'Recepción IA (panel de la web)',
        hintSmartai: 'Panel interactivo en la web pública: cita, llamada, receta y preguntas (base de conocimiento).',
        hint: 'Los cambios se aplican de inmediato a todos los usuarios de esta instalación — sin reinicio. Los datos existentes se conservan y reaparecen al reactivar el módulo.',
        saved: 'Features guardados.',
        packages: {
          title: 'Elegir paquete',
          active: 'Paquete activo',
          website: 'Web de la clínica',
          websiteHint: '39 €/mes — solo web, sin módulos de administración',
          booking: 'Web + reserva online',
          bookingHint: '69 €/mes — citas, calendario, Google-Sync, recordatorios',
          platform: 'Plataforma completa',
          platformHint: '99 €/mes — todos los módulos',
          hint: 'Un clic establece y guarda todos los módulos del paquete al instante. Después se pueden ajustar módulos individuales más abajo.',
        },
      },
      // Envío de correo (SMTP)
      smtpTitle: 'Envío de correo (SMTP)',
      smtpDescription: 'Configuración para correos salientes (confirmaciones de cita, cancelaciones). Cuenta de correo de All-Inkl en <code>info@seadent.es</code>.',
      host: 'Host',
      port: 'Puerto',
      portHint: '587 = STARTTLS (compatible con Hetzner), 465 = SSL (bloqueado por Hetzner).',
      user: 'Usuario',
      password: 'Contraseña',
      passwordStored: '(guardada)',
      passwordPlaceholderStored: '•••••• (dejar vacío → sin cambios)',
      passwordPlaceholderEnter: 'Introduzca la contraseña',
      secureHint: 'Usar SSL directamente (<code>secure=true</code>): solo tiene sentido con el puerto 465. Con el 587, déjelo desactivado (STARTTLS).',
      // Correo de prueba
      testEmailTitle: 'Enviar correo de prueba',
      testEmailDescription: 'Comprueba la conexión SMTP con los valores <strong>guardados</strong> actuales y envía un pequeño correo de prueba.',
      testRecipientPlaceholder: 'destinatario@example.com',
      sendTest: 'Enviar prueba',
      // Toasts / Errores
      errorLoadEmail: 'No se pudieron cargar los ajustes de correo; se han rellenado los valores predeterminados.',
      emailSaved: 'Ajustes de correo guardados.',
      errorSave: 'Error al guardar.',
      errorTestRecipient: 'Indique una dirección de destinatario para la prueba.',
      testAccepted: 'Correo de prueba aceptado ({{id}}).',
      errorTest: 'Error en la prueba.',
      errorTestDetail: 'Error en la prueba: {{detail}}',
      // Autoservicio (los pacientes cancelan / cambian la cita por enlace)
      selfservice: {
        title: 'Autoservicio para pacientes',
        description: 'Los pacientes pueden cancelar o cambiar su cita ellos mismos mediante el enlace «Gestionar cita» de los correos de confirmación y recordatorio. Aquí se definen los plazos y límites.',
        enabled: 'Autoservicio activo (los enlaces de los correos funcionan)',
        cancelMinHours: 'Plazo de cancelación (horas)',
        cancelMinHoursHint: 'Las cancelaciones en línea son posibles hasta tantas horas antes de la cita.',
        rescheduleMinHours: 'Plazo de cambio (horas)',
        rescheduleMinHoursHint: 'El cambio de fecha en línea es posible hasta tantas horas antes de la cita.',
        maxReschedules: 'Cambios máximos',
        maxReschedulesHint: 'Cuántas veces puede un paciente cambiar la misma cita.',
        linkHint: 'Dentro del plazo, los pacientes ven en su lugar el teléfono de la clínica. Si se desactiva, la página solo muestra los detalles de la cita.',
        saved: 'Ajustes de autoservicio guardados.',
        errorLoad: 'No se pudieron cargar los ajustes de autoservicio; se han rellenado los valores predeterminados.',
      },
      // Vacaciones / cierre de la clínica (bloquear la reserva online)
      closure: {
        title: 'Vacaciones / cierre',
        description: 'Bloquea la reserva de citas desde la web: asistente de reserva, panel de IA y el cambio de cita por enlace de autoservicio. Los visitantes ven un aviso con el teléfono y WhatsApp. En el área de administración todo sigue reservable, así que las citas por teléfono no se ven afectadas.',
        enabled: 'Bloquear la reserva online en el periodo indicado',
        from: 'Desde (opcional)',
        fromHint: 'En blanco = a partir de ahora. Con fecha se pueden programar las vacaciones con antelación.',
        until: 'Hasta el día (incluido)',
        untilHint: 'Último día bloqueado. Después la reserva online vuelve a abrirse sola.',
        statusActive: 'Activo: la reserva online está bloqueada hasta el {{until}} incluido.',
        statusUpcoming: 'Programado: el bloqueo empieza el {{from}} y termina el {{until}}.',
        statusInactive: 'Sin bloqueo: la reserva online funciona con normalidad.',
        statusExpired: 'El periodo indicado ya pasó: la reserva online vuelve a funcionar. El interruptor puede quedarse activado.',
        noticeHint: 'Las urgencias también quedan bloqueadas online, pero por teléfono se atiende todo. Las citas ya reservadas se mantienen y no se cancelan.',
        saved: 'Ajustes de vacaciones guardados.',
        errorLoad: 'No se pudieron cargar los ajustes de vacaciones; se han rellenado los valores predeterminados.',
      },
    },
  },
};
